import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import * as _ from "lodash";
import { of } from "rxjs";
import { catchError, map, switchMap, withLatestFrom } from "rxjs/operators";
import { AdminProductsService } from "../../../pages/admin-pages/products/services/admin-products.service";
import { ProductResponse } from "../../../pages/admin-pages/products/models/product-models";
import { AdminProductsActions } from "../../actions/admin-products.actions";
import { FailState } from "../../models/fail-state";
import { ProductsState } from "../../models/mp-admin/products-state";
import { AppState } from "../../reducers";
import { AdminProductsSelectors } from "../../selectors/admin-products.selectors";

@Injectable()
export class ProductsEffects {
	products = createEffect(() =>
		this.actions.pipe(
			ofType(AdminProductsActions.products.get),
			withLatestFrom(this.store.select(AdminProductsSelectors.products)),
			switchMap(([action, state]) =>
				this.canUseStoreValue(state, action.familyId)
					? of(AdminProductsActions.products.getSuccess({ state, familyId: action.familyId }))
					: of(AdminProductsActions.products.getApi({ familyId: action.familyId }))
			)
		)
	);

	productsApi = createEffect(() =>
		this.actions.pipe(
			ofType(AdminProductsActions.products.getApi),
			switchMap((action) =>
				this.service.getProducts(action.familyId, undefined, undefined).pipe(
					map((products) => {
						// Build products by family map for efficient filtering
						const productsByFamily: { [familyId: string]: ProductResponse[] } = {};
						products.forEach((product) => {
							if (!productsByFamily[product.productFamilyId]) {
								productsByFamily[product.productFamilyId] = [];
							}
							productsByFamily[product.productFamilyId].push(product);
						});

						return AdminProductsActions.products.getSuccess({
							state: { products, productsByFamily, loading: false, hasLoaded: true },
							familyId: action.familyId
						});
					}),
					catchError((error) =>
						of(AdminProductsActions.products.getFail(new FailState(error)))
					)
				)
			)
		)
	);

	constructor(
		private actions: Actions,
		private service: AdminProductsService,
		private store: Store<AppState>
	) {}

	private canUseStoreValue(state: ProductsState, familyId?: string): boolean {
		if (
			state === undefined ||
			_.has(state, "error") ||
			state.loading ||
			state.hasLoaded !== true ||
			!_.has(state, "products") ||
			state.products === undefined
		) {
			return false;
		}

		// If requesting all products, check if data has loaded
		if (!familyId) {
			return true;
		}

		// If requesting for specific family, check if we have the index built
		return (
			_.has(state, "productsByFamily") &&
			state.productsByFamily !== undefined &&
			_.has(state.productsByFamily, familyId)
		);
	}
}
